import { ActionReducerMapBuilder } from '@reduxjs/toolkit';

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import { filterUnsupportedFilters } from '@app/helpers/listing.helpers';
import { BaseSearchState, defaultSearchCriteria, defaultSearchFilters } from '@app/models';
import {
    clearUnsupportedSearchFiltersAction,
    initSearchFromUrlAction,
    resetCampaignListingIdAction,
    resetPageAction,
    resetSearchCriteriaAction,
    resetSearchFiltersAction,
    setFiltersVisibilityAction,
    setPageAction,
    setSearchLoadingAction,
    toggleFiltersVisibilityAction,
    updateSearchCriteriaAction,
    updateSearchFiltersAction,
} from '@app/state/slices/search-state.actions';

export function searchExtraReducers<State extends BaseSearchState>(
    builder: ActionReducerMapBuilder<State>,
) {
    builder.addCase(initSearchFromUrlAction, (state, { payload }) => {
        const { searchCriteria, filters, campaignListingId, page } = payload;

        state.campaignListingId = campaignListingId?.length ? campaignListingId : undefined;

        state.searchCriteria = {
            ...state.searchCriteria,
            ...searchCriteria,
        };

        state.filters = {
            ...state.filters,
            ...filters,
        };

        state.page = page || 1;
        state.meta.hasSearchChanged = false;
        state.meta.hasGeoChanged = false;
        state.meta.lastSearchTimestamp = Date.now();
    });

    builder.addCase(updateSearchCriteriaAction, (state, { payload }) => {
        const prevCriteria = state.searchCriteria;
        const newCriteria = {
            ...state.searchCriteria,
            ...payload.criteria,
        };
        state.meta.hasSearchChanged = !isEqual(prevCriteria, newCriteria);
        state.meta.hasGeoChanged =
            !isEqual(prevCriteria.geoId, newCriteria.geoId) ||
            !isEqual(prevCriteria.locationType, newCriteria.locationType);

        if (!state.meta.hasSearchChanged) {
            return;
        }

        state.searchCriteria = newCriteria;
    });

    builder.addCase(setPageAction, (state, { payload }) => {
        const newPage = payload.page || 1;

        state.meta.hasSearchChanged = state.page !== newPage;

        if (!state.meta.hasSearchChanged) {
            return;
        }

        state.page = newPage;
    });

    builder.addCase(updateSearchFiltersAction, (state, { payload }) => {
        const prevFilters = cloneDeep(state.filters);
        const newFilters = {
            ...prevFilters,
            ...payload.filters,
        };
        state.meta.hasSearchChanged = !isEqual(prevFilters, newFilters);

        if (!state.meta.hasSearchChanged) {
            return;
        }

        state.filters = { ...newFilters };
        state.page = 1;
    });

    builder.addCase(clearUnsupportedSearchFiltersAction, state => {
        const newFilters = filterUnsupportedFilters(state.filters);

        if (!isEqual(state.filters, newFilters)) {
            state.filters = { ...newFilters };
        }
    });

    builder.addCase(resetSearchCriteriaAction, state => {
        state.searchCriteria = cloneDeep(defaultSearchCriteria);
        state.meta.hasGeoChanged = true;
        state.meta.hasSearchChanged = true;
    });

    builder.addCase(resetSearchFiltersAction, state => {
        state.filters = defaultSearchFilters;
        state.meta.hasSearchChanged = true;
    });

    builder.addCase(resetCampaignListingIdAction, state => {
        state.campaignListingId = undefined;
        state.meta.hasSearchChanged = true;
    });

    builder.addCase(resetPageAction, state => {
        state.page = 1;
        state.meta.hasSearchChanged = true;
    });

    builder.addCase(setSearchLoadingAction, (state, { payload }) => {
        state.meta.isLoading = payload;
        if (payload === false) {
            state.meta.lastSearchTimestamp = Date.now();
        }
    });

    builder.addCase(toggleFiltersVisibilityAction, state => {
        state.isFiltersContentVisible = !state.isFiltersContentVisible;
    });

    builder.addCase(setFiltersVisibilityAction, (state, { payload }) => {
        state.isFiltersContentVisible = payload.open;
    });
}
